import FeatherIcon from 'components/base/FeatherIcon';
import Badge from 'components/base/Badge';
import { Announcement } from 'providers/vortex-schemas';
import { Table } from 'react-bootstrap';
import { Link } from 'react-router';
import { clusterLabel, simdLabel } from './features';

export const ANNOUNCEMENT_KIND: Record<string, string> = {
  restart: 'Restart',
  upgrade: 'Upgrade',
  downgrade: 'Downgrade',
  feature: 'Feature activation',
  security: 'Security',
  other: 'Announcement'
};

/** A CVE's public record. */
export const cveUrl = (id: string) =>
  `https://www.cve.org/CVERecord?id=${encodeURIComponent(id)}`;

/** "Oct 31, 12:07 AM" (the year too when it isn't this one). */
export const postedWhen = (unixS: number) => {
  const d = new Date(unixS * 1000);
  return d.toLocaleString('en-US', {
    ...(d.getFullYear() !== new Date().getFullYear()
      ? { year: 'numeric' }
      : {}),
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

/** Which client a version is: Firedancer's are calendar versions
 *  ("26.10.4"), Agave's semver ("4.4.0-rc.1"). */
export const clientOfVersion = (v: string) =>
  parseInt(v.replace(/^v/, ''), 10) >= 20 ? 'Firedancer' : 'Agave';

/** "Agave 4.4.0-beta.1 · Firedancer 26.10.4": the post's versions, by
 *  client. */
const byClient = (versions: string[]) => {
  const groups = new Map<string, string[]>();
  for (const v of versions)
    groups.set(clientOfVersion(v), [
      ...(groups.get(clientOfVersion(v)) ?? []),
      v
    ]);
  return [...groups].map(([c, vs]) => `${c} ${vs.join(', ')}`).join(' · ');
};
const versionsText = (a: Announcement) =>
  a.minVersions.length
    ? `at least ${byClient(a.minVersions)}`
    : byClient(a.versions);

/** The clients a post's versions are for. */
export const clientsOf = (a: Announcement) => [
  ...new Set([...a.versions, ...a.minVersions].map(clientOfVersion))
];

/** A release candidate ("4.4.0-rc.1"): an upgrade for some of the stake
 *  to try first, not for everyone. */
export const isCandidate = (a: Announcement) =>
  a.kind === 'upgrade' &&
  (a.candidate ??
    (a.versions.length > 0 && a.versions.every(v => /-rc\b/i.test(v))));

/** "for 10% of stake" once the API reads the post's target, else that a
 *  release candidate is for some of the stake; a release (not a
 *  candidate, not a test) is for every validator. */
const candidateText = (a: Announcement) =>
  a.stakeTarget != null && a.stakeTarget < 1
    ? `for ${Math.round(a.stakeTarget * 100)}% of stake`
    : isCandidate(a)
      ? 'release candidate: for some of the stake, not all'
      : a.kind === 'upgrade' && !a.testRun
        ? 'ready for all validators'
        : null;

/** A test run stands apart; any other upgrade or downgrade post is
 *  replaced by a later one for the same client (4.4.0-rc.1 for 10% of
 *  stake, then for 20%, then 4.4.0 for everyone: only the last matters). */
export const isSuperseded = (a: Announcement, all: Announcement[]) => {
  if ((a.kind !== 'upgrade' && a.kind !== 'downgrade') || a.testRun)
    return false;
  const clients = clientsOf(a);
  return all.some(
    b =>
      b !== a &&
      b.cluster === a.cluster &&
      (b.kind === 'upgrade' || b.kind === 'downgrade') &&
      !b.testRun &&
      b.postedAt > a.postedAt &&
      clientsOf(b).some(c => clients.includes(c))
  );
};

/** "Agave upgrade" (or "Agave upgrade candidate", "Agave downgrade"): an
 *  upgrade or downgrade post's kind, with its client when it names only
 *  one. A downgrade's versions are the releases to move back to. */
export const announcementTitle = (a: Announcement) => {
  const kind = ANNOUNCEMENT_KIND[a.kind] ?? a.kind;
  const clients = clientsOf(a);
  // A test run (switching release lines as an exercise): "... test".
  const test = a.testRun ? ' test' : '';
  if (a.kind === 'downgrade')
    return clients.length === 1
      ? `${clients[0]} downgrade${test}`
      : `Downgrade${test}`;
  if (a.kind === 'upgrade')
    return clients.length === 1
      ? `${clients[0]} upgrade${test || (isCandidate(a) ? ' candidate' : '')}`
      : `Upgrade${test}`;
  return kind;
};

/**
 * Announcements from Solana Tech's announcement channels, newest first:
 * when, which cluster, what kind, and the facts read from the post (its
 * versions, epochs, SIMDs), linking the original post. Only the facts
 * are kept, never the post's text.
 */
const AnnouncementsTable = ({
  announcements,
  showCluster = true
}: {
  announcements: Announcement[];
  showCluster?: boolean;
}) => (
  <>
    {/* Phones: the short list (four columns don't fit). */}
    <div className="d-md-none">
      <AnnouncementList
        announcements={announcements}
        showCluster={showCluster}
      />
    </div>
    <Table
      responsive
      size="sm"
      className="data-table compact-rows fs-9 mb-0 d-none d-md-table"
    >
      <thead>
        <tr>
          <th>Posted</th>
          {showCluster && <th>Cluster</th>}
          <th>Kind</th>
          <th>Says</th>
          <th className="text-end">Post</th>
        </tr>
      </thead>
      <tbody>
        {announcements.map(a => (
          <tr key={`${a.cluster}-${a.id}`}>
            <td className="text-nowrap text-body-tertiary">
              {postedWhen(a.postedAt)}
            </td>
            {showCluster && (
              <td className="text-nowrap">{clusterLabel(a.cluster)}</td>
            )}
            <td className="text-nowrap">
              {announcementTitle(a)}
              {a.urgent && (
                <Badge bg="danger" className="ms-2">
                  Urgent
                </Badge>
              )}
            </td>
            <td className="text-body-secondary">
              {[
                a.restart?.lastConfirmedSlot != null &&
                  `halted chain's last confirmed slot ${a.restart.lastConfirmedSlot.toLocaleString()}`,
                a.restart?.finished === true && 'finished',
                (a.versions.length > 0 || a.minVersions.length > 0) &&
                  versionsText(a),
                candidateText(a),
                a.epochs.length > 0 &&
                  `epoch ${a.epochs.map(e => e.toLocaleString()).join(', ')}`
              ]
                .filter(Boolean)
                .join(' · ') || '—'}
              {a.simds.length > 0 && (
                <>
                  {' · '}
                  {a.simds.map((n, i) => (
                    <span key={n}>
                      {i > 0 && ', '}
                      <Link to={`/simd/${n}`}>{simdLabel(n)}</Link>
                    </span>
                  ))}
                </>
              )}
              {(a.cves?.length ?? 0) > 0 && (
                <>
                  {' · '}
                  {a.cves!.map((id, i) => (
                    <span key={id}>
                      {i > 0 && ', '}
                      <a href={cveUrl(id)} target="_blank" rel="noreferrer">
                        {id} ↗
                      </a>
                    </span>
                  ))}
                </>
              )}
            </td>
            <td className="text-end text-nowrap">
              {a.url ? (
                <a href={a.url} target="_blank" rel="noreferrer">
                  Discord <FeatherIcon icon="arrow-up-right" size={11} />
                </a>
              ) : (
                '—'
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  </>
);

export default AnnouncementsTable;

/** "Restart · 4.4.0-beta.1, 26.10.4": what a post is, in a line. */
export const summary = (a: Announcement) =>
  [
    a.restart?.lastConfirmedSlot != null &&
      `last confirmed slot ${a.restart.lastConfirmedSlot.toLocaleString()}`,
    a.restart?.finished === true && 'finished',
    (a.versions.length > 0 || a.minVersions.length > 0) && versionsText(a),
    candidateText(a),
    a.epochs.length > 0 &&
      `epoch ${a.epochs.map(e => e.toLocaleString()).join(', ')}`,
    (a.cves?.length ?? 0) > 0 && a.cves!.join(', ')
  ]
    .filter(Boolean)
    .join(' · ');

/** The latest announcements as a short list: kind, when, what it says,
 *  linking the post. */
export const AnnouncementList = ({
  announcements,
  showCluster = false
}: {
  announcements: Announcement[];
  /** Name each post's cluster (a list of both). */
  showCluster?: boolean;
}) => (
  <ul className="list-unstyled mb-0 d-flex flex-column gap-2 fs-9">
    {announcements.map(a => (
      <li key={`${a.cluster}-${a.id}`}>
        <div className="d-flex align-items-baseline gap-2">
          {a.url ? (
            <a
              href={a.url}
              target="_blank"
              rel="noreferrer"
              className="fw-semibold"
            >
              {announcementTitle(a)}
            </a>
          ) : (
            <span className="fw-semibold">{announcementTitle(a)}</span>
          )}
          {a.urgent && <Badge bg="danger">Urgent</Badge>}
          <span className="text-body-tertiary fs-10 ms-auto text-nowrap">
            {showCluster && `${clusterLabel(a.cluster)} · `}
            {postedWhen(a.postedAt)}
          </span>
        </div>
        {summary(a) && (
          <div className="text-body-tertiary fs-10">{summary(a)}</div>
        )}
      </li>
    ))}
  </ul>
);
